import { SCRIM_GRADIENT_HEAVY } from '@/components/page-builder/blocks/scrimGradient';
import { cn } from '@/lib/utils';
import { Link } from '@inertiajs/react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { AuthorByline } from './AuthorByline';
import { articleUrl, itemVariants, sectionColors, sectionVariants } from './constants';
import type { JournalGuide, JournalSection } from './types';

const colors = sectionColors('ivory');

function MagazineLeadCard({ guide }: { guide: JournalGuide }) {
    const tag = guide.kicker ?? guide.tags?.[0]?.name ?? guide.country_region;

    return (
        <Link
            href={articleUrl(guide.slug)}
            className="group relative flex min-h-[60vh] w-full flex-col justify-end overflow-hidden bg-[#0e2727] lg:h-full lg:min-h-0"
        >
            {guide.main_image && (
                <div className="absolute inset-0">
                    <img
                        src={guide.main_image}
                        alt={guide.title}
                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                        loading="lazy"
                    />
                </div>
            )}
            {guide.main_image && <div className="absolute inset-0" style={{ background: SCRIM_GRADIENT_HEAVY }} />}
            <div className="relative z-10 max-w-2xl p-8 pb-12 lg:p-12 lg:pb-16">
                {tag && <p className="lux-caps lux-caps-sm mb-3 text-[#F5F5F5]/80">{tag}</p>}
                <h3 className="lux-didot lux-didot-xl text-[#F5F5F5]">{guide.title}</h3>
                {guide.excerpt && <p className="lux-body lux-body-md mt-4 leading-relaxed text-[#F5F5F5]/60">{guide.excerpt}</p>}
                <span className="lux-caps lux-caps-sm mt-6 inline-flex items-center justify-center rounded-sm border border-white/30 bg-black/15 px-5 py-2 text-white backdrop-blur-sm transition-colors duration-300 hover:border-white/50 hover:bg-black/25">
                    Read Story
                </span>
            </div>
        </Link>
    );
}

function ArticleCard({ guide }: { guide: JournalGuide }) {
    const tag = guide.kicker ?? guide.country_region ?? guide.tags?.[0]?.name;

    return (
        <Link href={articleUrl(guide.slug)} className="group flex h-full flex-col px-3 py-3 lg:px-4 lg:py-4">
            {guide.main_image && (
                <div className="aspect-[4/3] w-full overflow-hidden">
                    <img
                        src={guide.main_image}
                        alt={guide.title}
                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                        loading="lazy"
                    />
                </div>
            )}
            <div className="mt-3 lg:mt-4">
                {tag && <p className="lux-caps lux-caps-sm text-[#0e2727]/50">{tag}</p>}
                <h4 className="lux-didot lux-didot-md mt-1.5 text-[#0e2727] transition-colors group-hover:text-[#0e2727]/75">{guide.title}</h4>
                {guide.excerpt && <p className="lux-body lux-body-sm mt-2 line-clamp-2 text-[#0e2727]/60">{guide.excerpt}</p>}
                {guide.author_name && <AuthorByline name={guide.author_name} className="mt-3 text-[#0e2727]/55" />}
            </div>
        </Link>
    );
}

interface SectionMagazineProps {
    section: JournalSection;
    /**
     * When true, skip the section header (kicker / title / divider / "See all").
     * Useful for rendering just the magazine grid inside other layouts
     * (e.g. homepage features strip).
     */
    minimal?: boolean;
}

export default function SectionMagazine({ section, minimal = false }: SectionMagazineProps) {
    const shouldReduceMotion = useReducedMotion();
    const metaKicker = section.kicker;

    if (section.guides.length === 0) return null;

    const [lead, ...rest] = section.guides;
    const gridCards = rest.slice(0, 4);
    const hasMore = section.totalCount > 5;

    return (
        <motion.section
            id={section.tag}
            className="scroll-mt-28 bg-[#fdfaf3] md:scroll-mt-32"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={shouldReduceMotion ? undefined : sectionVariants}
        >
            {/* Section header — skipped in minimal mode */}
            {!minimal && (
                <div className="lux-space-px">
                    <div className="mx-auto max-w-[1400px] pt-24 lg:pt-32">
                        <motion.div className="mb-14" variants={shouldReduceMotion ? undefined : itemVariants}>
                            {metaKicker && <span className={cn('lux-caps lux-caps-sm mb-2 block', colors.kicker)}>{metaKicker}</span>}
                            <div className="flex items-baseline justify-between gap-6">
                                <h2 className={cn('lux-didot', colors.heading)} style={{ fontSize: 'clamp(2.75rem, 6.5vw, 5rem)', lineHeight: 1.05 }}>
                                    {section.name}
                                </h2>
                                {hasMore && (
                                    <Link
                                        href={`/journal/category/${section.tag}`}
                                        className={cn('lux-caps lux-caps-sm inline-flex shrink-0 items-center gap-1.5 transition-colors', colors.link)}
                                    >
                                        See all
                                        <ArrowRight className="h-3.5 w-3.5" />
                                    </Link>
                                )}
                            </div>
                            <div className="mt-5 flex items-center gap-0">
                                <div className={cn('h-0.5 w-12', colors.decorativeRule)} />
                                <div className={cn('h-px flex-1', colors.decorativeRule, 'opacity-50')} />
                            </div>
                        </motion.div>
                    </div>
                </div>
            )}

            {/* Magazine grid: lead + 2×2 */}
            <motion.div
                className={cn(
                    'grid min-h-[50vh] bg-[#fdfaf3] pb-16 lg:grid-cols-2 lg:pb-24',
                    minimal && 'pt-16 lg:pt-24',
                )}
                variants={shouldReduceMotion ? undefined : itemVariants}
            >
                {/* Lead — full-bleed image with scrim */}
                <MagazineLeadCard guide={lead} />

                {/* Right: 2×2 article grid */}
                {gridCards.length > 0 && (
                    <div
                        className={cn(
                            'grid bg-[#fdfaf3] px-5 pt-8 pb-5 lg:px-8 lg:pt-0 lg:pb-8',
                            gridCards.length >= 3 ? 'grid-cols-2 grid-rows-2' : gridCards.length === 2 ? 'grid-cols-2 grid-rows-1' : 'grid-cols-1',
                        )}
                    >
                        {gridCards.map((guide) => (
                            <motion.div key={guide.id} variants={shouldReduceMotion ? undefined : itemVariants}>
                                <ArticleCard guide={guide} />
                            </motion.div>
                        ))}
                    </div>
                )}
            </motion.div>
        </motion.section>
    );
}
